// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import { ErrorList } from 'domains/models';
import { workflowTypeToAnswerType } from 'domains/models/answer/answerType';
import { UserType } from 'domains/models/form_user/user_type';
import Email from 'domains/models/user/email';
import PhoneNumber from 'domains/models/form_answer/phone_number';
import Workflow from 'domains/models/workflow';
import { WorkflowType } from 'domains/models/workflow/workflowType';
import { patch, post } from 'infras/rest_client';
import { useRouter } from 'next/router';
import {
  FormAnswerCreateBody,
  FormAnswerCreateBodyItem,
  FormAnswerCreateResultItem,
  FormAnswerUpdateBody,
  FormAnswerUpdateBodyItem,
} from 'presentations/api/schema/answer';
import { FormPageItem } from 'presentations/api/schema/form';
import { TeamItem } from 'presentations/api/schema/team';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  DashboardPortalContext,
  setErrorMessage,
} from 'presentations/reducers/DashboardPortal';
import {
  apiPath,
  formCompletePath,
  formWidgetCompletePath,
  guestFormAnswerEditCompletePath,
} from 'presentations/routes/path';
import { setEvent } from 'presentations/views/helpers/analytics';
import { GaTracker } from 'presentations/views/helpers/analytics/user_tracking/ga';
import { relativeLuminanceW3C } from 'presentations/views/helpers/color';
import { eventTypeThemeColors } from 'domains/models/event_type/color';
import {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { currentLocale } from 'infras/datetime';
import { FormAnswerItem } from 'presentations/api/schema/form_answer';
import { ListViewType } from 'domains/models/form/listViewType';
import { otherSelectValue } from 'domains/models/answer';
import { Status } from 'domains/models/form/status';
import { MetaPixelTracker } from 'presentations/views/helpers/analytics/user_tracking/meta_pixel';
import { addCampaignCode } from 'presentations/routes/helper';

export interface Props {
  viewer: ViewerItem | null;
  form: FormPageItem;
  formAnswer?: FormAnswerItem;
  team: TeamItem;
  headerTeamImage: string | null;
  isWidget: boolean;
}
export function useFormPage(props: Props) {
  const t = translateInComponent(translations);
  const [errors, setErrors] = useState<ErrorList>({});
  const [isShouldValidateAll, setIsShouldValidateAll] = useState<boolean>(
    false,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const customQuestions = props.form.workflows || [];

  const [customQuestionAnswers, setCustomQuestionAnswers] = useState<any[]>(
    new Array(customQuestions.length),
  );

  const [customOtherAnswers, setCustomOtherAnswers] = useState<string[]>(
    new Array(customQuestions.length),
  );
  const [email, setEmail] = useState<string | null>(
    props.viewer ? props.viewer.email : null,
  );
  const [name, setName] = useState<string | null>(
    props.viewer ? props.viewer.name : null,
  );
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [isDisabled, setIsDisabled] = useState<boolean>(!props.formAnswer);
  const [page, setPage] = useState<number>(0);

  const navigationContext = useContext(DashboardPortalContext);
  const isFullWidth = props.form.listViewType === ListViewType.FullScreen;
  const router = useRouter();

  const resizeWidgetFrame = useCallback(() => {
    parent.postMessage(
      {
        name: 'windowHeight',
        value: document.documentElement.offsetHeight,
      },
      '*',
    );
  }, []);

  useEffect(() => {
    if (props.isWidget) {
      resizeWidgetFrame();
      window.addEventListener('resize', () => {
        resizeWidgetFrame();
      });
    }
  }, []);

  useEffect(() => {
    setEvent(
      'form page view',
      {
        planId: props.form.planId,
        listViewType: props.form.listViewType,
        visualViewType: props.form.visualViewType,
        teamUid: props.team.uid,
        formUid: props.form.uid,
        isLoginViewer: !!props.viewer,
        isInTeamView: props.team.uid === props.viewer?.team?.uid,
      },
      { isAmplitude: false },
    );
  }, [props.form]);

  useEffect(() => {
    if (!props.form) return;
    const answers = [...customQuestionAnswers];
    if (props.formAnswer) {
      setEmail(props.formAnswer.email);
      setName(props.formAnswer.name);
      setPhoneNumber(props.formAnswer.phoneNumber);
      props.formAnswer.answers.map((answer, index) => {
        answers[index] = answer.contentId || answer.content;
      });
    }

    if (props.viewer) {
      for (let index = 0; index < customQuestions.length; index++) {
        const q = customQuestions[index];
        if (q.workflowType === WorkflowType.QuestionAnswerEmail) {
          answers[index] = props.viewer.email;
        } else if (q.workflowType === WorkflowType.QuestionAnswerName) {
          answers[index] = props.viewer.name;
        }
      }
    }
    setCustomQuestionAnswers(answers);
    setIsShouldValidateAll(true);
  }, [props.formAnswer, props.form, props.viewer]);

  useEffect(() => {
    if (isShouldValidateAll) {
      validateAll(false);
      setIsShouldValidateAll(false);
    }
  }, [isShouldValidateAll]);

  const isHost = useMemo(() => {
    const v = props.viewer;
    if (!v) return false;
    return !!props.form.users.find(
      (u) =>
        props.form.teamId === v.team?.id &&
        u.uid === v.uid &&
        u.userType === UserType.Host,
    );
  }, [props.viewer, props.form]);

  const onSubmit = async () => {
    if (!validateAll(true)) {
      setIsLoading(true);
      // 少しローディングさせてから戻す
      setTimeout(() => {
        navigationContext.dispatch(setErrorMessage(t('Error Required')));
        setIsLoading(false);
      }, 1000);
      return;
    }

    // カスタム質問
    const answers: FormAnswerCreateBodyItem[] = [];
    if (props.form) {
      customQuestions.forEach((wf, index) => {
        const answerType = workflowTypeToAnswerType(wf.workflowType);
        if (!answerType) return;

        let content = customQuestionAnswers[index];
        let contentId: any = null;

        if (wf.workflowType === WorkflowType.QuestionAnswerCheckboxes) {
          const contentIds =
            content && Array.isArray(content) ? [...content] : [];
          contentId = [];
          content = [];
          if (wf.choices) {
            for (const c of wf.choices) {
              if (contentIds.includes(c.uid)) {
                contentId.push(c.uid);
                content.push(c.value);
              }
            }
          }
        } else {
          if (wf.choices) {
            const choice = wf.choices.find((c) => {
              return c.uid === content;
            });
            if (choice) {
              contentId = choice.uid;
              content = choice.value;
            }
          }
        }

        if (wf.hasOtherChoice) {
          if (
            wf.workflowType === WorkflowType.QuestionAnswerCheckboxes &&
            Array.isArray(customQuestionAnswers[index]) &&
            customQuestionAnswers[index].includes(otherSelectValue)
          ) {
            const customOtherAnswer = customOtherAnswers[index];
            content.push(customOtherAnswer);
            contentId.push(otherSelectValue);

            if (customOtherAnswer) {
            }
          } else if (
            (wf.workflowType === WorkflowType.QuestionAnswerMultipleChoice ||
              wf.workflowType === WorkflowType.QuestionAnswerDropDown) &&
            customQuestionAnswers[index] === otherSelectValue
          ) {
            const customOtherAnswer = customOtherAnswers[index];
            if (customOtherAnswer) {
              content = customOtherAnswer;
              contentId = otherSelectValue;
            }
          }
        }

        const question: any = wf.content;
        answers.push({
          formId: props.form.id,
          question,
          answerType,
          content,
          contentId,
          workflowId: wf.uid,
        });
      });
    }
    setIsLoading(true);

    let result;
    if (props.formAnswer) {
      const updateAnswers: FormAnswerUpdateBodyItem[] = answers.map(
        (a, index) => {
          return {
            id: props.formAnswer!.answers[index].id,
            question: a.question,
            answerType: a.answerType,
            content: a.content,
            contentId: a.contentId,
            workflowId: a.workflowId,
          };
        },
      );
      const data: FormAnswerUpdateBody = {
        email,
        name,
        phoneNumber,
        answers: updateAnswers,
        locale: currentLocale(),
      };
      result = await patch(
        apiPath(
          `/v1/forms/${props.form.uid}/answers/${props.formAnswer.uid}/update`,
        ),
        { data },
      );
      if (!result.isSuccess) {
        setErrors(result.errors);
        setIsLoading(false);
        return;
      }
      router.push(
        guestFormAnswerEditCompletePath(props.form.uid, props.formAnswer.uid),
      );
    } else {
      const data: FormAnswerCreateBody = {
        email,
        name,
        phoneNumber,
        answers,
        locale: currentLocale(),
      };
      result = await post(
        apiPath(`/v1/forms/${props.form.uid}/answers/create`),
        { data },
      );
      // setIsLoading(false);
      // return;
      if (!result.isSuccess) {
        setErrors(result.errors);
        setIsLoading(false);
        return;
      }
      const {
        forwardablePath,
        redirectUrl,
        uid,
      }: FormAnswerCreateResultItem = result.data;
      setEvent('form submit', {
        isWidget: props.isWidget,
      });
      GaTracker.inviteeSubmitsRoutingForm();
      MetaPixelTracker.inviteeSubmitsRoutingForm();

      if (redirectUrl) {
        if (props.isWidget) {
          parent.postMessage(
            {
              name: 'redirectUrl',
              value: redirectUrl,
            },
            '*',
          );
        } else {
          window.location.href = getUrlWithForwardableParams(redirectUrl);
        }
      } else if (forwardablePath) {
        if (props.isWidget) {
          parent.postMessage(
            {
              name: 'redirectUrl',
              value: forwardablePath,
            },
            '*',
          );
        } else {
          window.location.href = getUrlWithForwardableParams(forwardablePath);
        }
      } else {
        if (props.isWidget) {
          router.push(formWidgetCompletePath(props.form.uid, uid));
        } else {
          router.push(formCompletePath(props.team.slug, props.form.uid, uid));
        }
      }
    }
  };

  const isComplete = page === props.form.workflows.length;

  const getUrlWithForwardableParams = (distUrl: string) => {
    return addCampaignCode(distUrl, {
      term: router.query.utm_term ? String(router.query.utm_term) : undefined,
      content: router.query.utm_content
        ? String(router.query.utm_content)
        : undefined,
      source: router.query.utm_source
        ? String(router.query.utm_source)
        : undefined,
      campaign: router.query.utm_campaign
        ? String(router.query.utm_campaign)
        : undefined,
      medium: router.query.utm_medium
        ? String(router.query.utm_medium)
        : undefined,
    });
  };

  const onSubmitForm = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isFullWidth) return;
    onClickNext();
  };

  const onClickNext = async () => {
    if (!isFullWidth) return;
    if (page !== 0) {
      const answerIndex = page - 1;
      const workflow = customQuestions[answerIndex];
      const key = `customQuestion.${answerIndex}`;
      const value = customQuestionAnswers[answerIndex];

      if (workflow.isRequired || value) {
        const propErrors = validate(key, value);
        if (propErrors.length > 0) {
          const newErrors = { ...errors };
          newErrors[key] = [...propErrors];
          setErrors(newErrors);
          return;
        }
      }
      if (value === otherSelectValue && workflow.isRequired) {
        const text = customOtherAnswers[answerIndex];
        if (!text) {
          const newErrors: ErrorList = {
            ...errors,
          };
          if (!newErrors[key]) {
            newErrors[key] = [];
          }
          newErrors[key].push('model.EventContact.custom.E001');
          setErrors(newErrors);
          return;
        }
      }
    }
    if (isComplete) {
      await onSubmit();
    } else {
      setPage(page + 1);
      const i = page;
      const newPropErrors = validate(
        `customQuestion.${i}`,
        customQuestionAnswers[i],
      );
      setIsDisabled(newPropErrors.length > 0);
    }
  };

  const onClickPrev = () => {
    if (page > 0) {
      setPage(page - 1);
      const i = page - 2;
      if (i < 0) {
        setIsDisabled(false);
        return;
      }
      const newPropErrors = validate(
        `customQuestion.${i}`,
        customQuestionAnswers[i],
      );
      setIsDisabled(newPropErrors.length > 0);
    }
  };

  const onChange = (e: any) => {
    setIsDisabled(false);
    let { name, value } = e.currentTarget;
    if (e.currentTarget.dataset) {
      name = name || e.currentTarget.dataset.name;
      value = value || e.currentTarget.dataset.value;
    }
    if (typeof name !== 'string' || !name.startsWith('customQuestion')) {
      setIsDisabled(true);
      return;
    }
    let updateValue = value;
    const index = Number(name.split('.')[1]);
    const newAnswers = [...customQuestionAnswers];
    const customQuestion = customQuestions[index];
    if (customQuestion.workflowType === WorkflowType.QuestionAnswerCheckboxes) {
      if (!newAnswers[index]) {
        newAnswers[index] = [updateValue];
        updateValue = newAnswers[index];
      } else if (typeof newAnswers[index] === 'object') {
        if (newAnswers[index].includes(updateValue)) {
          newAnswers[index].splice(newAnswers[index].indexOf(updateValue), 1);
        } else {
          newAnswers[index].push(updateValue);
        }
        updateValue = newAnswers[index];
      }
    } else {
      newAnswers[index] = updateValue;
    }
    setCustomQuestionAnswers(newAnswers);

    let propErrors = validate(name, updateValue);
    if (
      updateValue &&
      (updateValue === otherSelectValue ||
        updateValue.includes(otherSelectValue))
    ) {
      const otherErrors = validateOtherAnswer(
        name,
        updateValue,
        customOtherAnswers[index],
      );
      if (otherErrors.length > 0) {
        propErrors = [...propErrors, ...otherErrors];
      }
    }

    const newErrors = { ...errors };
    if (propErrors.length === 0) {
      delete newErrors[name];
    } else {
      newErrors[name] = propErrors;
    }
    setErrors(newErrors);
    if (isFullWidth) {
      if (Object.keys(newErrors).length > 0) {
        setIsDisabled(true);
      }
    } else {
      setIsShouldValidateAll(true);
    }
  };

  const validate = (name: string, value: any): string[] => {
    const customErrors: string[] = [];
    if (typeof name === 'string' && name.startsWith('customQuestion')) {
      const index = Number(name.split('.')[1]);
      const workflow = customQuestions[index];

      if (workflow.workflowType === WorkflowType.QuestionAnswerEmail) {
        if (value) {
          const newEmail = new Email(value);
          if (!newEmail.isValid()) {
            customErrors.push('model.EventContact.email.E002');
          }
        }
      } else if (
        workflow.workflowType === WorkflowType.QuestionAnswerPhoneNumber
      ) {
        if (value) {
          const newPhoneNumber = new PhoneNumber(value);
          if (!newPhoneNumber.isValid()) {
            customErrors.push('model.FormAnswer.phoneNumber.E001');
          }
        }
      }
      if (workflow.isRequired) {
        if (workflow.workflowType === WorkflowType.QuestionAnswerCheckboxes) {
          if (!Array.isArray(value) || value.length === 0) {
            customErrors.push('model.EventContact.custom.E001');
          }
        } else if (!value) {
          customErrors.push('model.EventContact.custom.E001');
        }
      }
    }
    return customErrors;
  };

  const onChangeOtherAnswer = (e: any) => {
    setIsDisabled(false);
    const { name, value } = e.currentTarget;
    if (typeof name !== 'string' && !name.startsWith('customQuestion')) {
      setIsDisabled(true);
      return;
    }
    const index = Number(name.split('.')[1]);
    const newOtherAnswers = [...customOtherAnswers];
    newOtherAnswers[index] = value;
    setCustomOtherAnswers(newOtherAnswers);

    const newAnswers = [...customQuestionAnswers];
    const customQuestion = customQuestions[index];
    if (value) {
      if (
        customQuestion.workflowType ===
        WorkflowType.QuestionAnswerMultipleChoice
      ) {
        newAnswers[index] = otherSelectValue;
        setCustomQuestionAnswers(newAnswers);
      } else if (
        customQuestion.workflowType === WorkflowType.QuestionAnswerCheckboxes
      ) {
        if (!newAnswers[index]) {
          newAnswers[index] = [otherSelectValue];
        } else if (typeof newAnswers[index] === 'object') {
          if (!newAnswers[index].includes(otherSelectValue)) {
            newAnswers[index].push(otherSelectValue);
          }
        }
        setCustomQuestionAnswers(newAnswers);
      }
    }
    const newErrors = { ...errors };
    const propErrors = validateOtherAnswer(
      `customQuestion.${index}`,
      customQuestionAnswers[index],
      value,
    );
    if (propErrors.length === 0) {
      delete newErrors[name];
    } else {
      newErrors[name] = propErrors;
    }
    setErrors(newErrors);
  };

  const validateOtherAnswer = (
    name: string,
    questionAnswer: any,
    otherAnswer: any,
  ): string[] => {
    const customErrors: string[] = [];
    const index = Number(name.split('.')[1]);
    if (
      customQuestions[index].isRequired &&
      customQuestions[index].hasOtherChoice
    ) {
      if (
        customQuestions[index].workflowType ===
        WorkflowType.QuestionAnswerCheckboxes
      ) {
        if (
          Array.isArray(questionAnswer) &&
          questionAnswer.some((q) => q === otherSelectValue) &&
          !otherAnswer
        ) {
          customErrors.push('model.EventContact.custom.E001');
        }
      } else {
        if (questionAnswer === otherSelectValue && !otherAnswer) {
          customErrors.push('model.EventContact.custom.E001');
        }
      }
    }
    return customErrors;
  };

  const validateAll = (isSetError: boolean): boolean => {
    setIsDisabled(true);
    const newErrors: ErrorList = {};
    customQuestions.forEach((q, i) => {
      const name = `customQuestion.${i}`;
      let propErrors = [
        ...validate(`customQuestion.${i}`, customQuestionAnswers[i]),
      ];

      if (q.isRequired && q.hasOtherChoice) {
        propErrors = [
          ...propErrors,
          ...validateOtherAnswer(
            `customQuestion.${i}`,
            customQuestionAnswers[i],
            customOtherAnswers[i],
          ),
        ];
      }
      if (propErrors.length > 0) {
        newErrors[name] = propErrors;
      }
    });
    if (isSetError) {
      setErrors(newErrors);
    }
    if (Object.keys(newErrors).length > 0) {
      return false;
    }
    setIsDisabled(false);
    return true;
  };

  const isLightColor = useMemo(
    () =>
      !eventTypeThemeColors.includes(props.form.color) &&
      relativeLuminanceW3C(props.form.color) >= 0.5,
    [props.form],
  );

  const isShowForm =
    props.form.isAvailableRoutingForm && props.form.status === Status.Enable;

  return {
    state: {
      isHost,
      customQuestions,
      customQuestionAnswers,
      customOtherAnswers,
      email,
      name,
      phoneNumber,
      errors,
      isLoading,
      isLightColor,
      isDisabled,
      page,
      isComplete,
      isShowForm,
    },
    actions: {
      onChange,
      onChangeOtherAnswer,
      onSubmit,
      onClickNext,
      onClickPrev,
      onSubmitForm,
    },
  };
}
